// ─── Calendriers d'un espace (suite) ─────────────────────────────
// Un espace peut porter PLUSIEURS calendriers :
//   • défaut : l'espace lui-même (calId = espaceId) — toujours présent ;
//   • suite  : docs calendriers/{id} avec un champ `espaceId` (créés ici) ;
//   • mopcal : calendriers MoPCal ADOPTÉS via espace.tenantsHerites (participants).
// Les événements/garde d'un calendrier vivent sous evenements/{calId} etc.
import { supprimerCalendrierCascade } from './suppressionCalendrier';
import { db, rangNiveau, accesDepuisMatrice, assurerGenerale, cleCible } from '@mop/core';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, setDoc, onSnapshot, getDoc,
  query, where,
} from 'firebase/firestore';

export const TYPES_CALENDRIER = [
  { id: 'general', label: 'Général', emoji: '📅' },
  { id: 'coparentalite', label: 'Co-parentalité', emoji: '👶' },
];

// Calendriers « suite » d'un espace (temps réel).
//
// `onErreur(message)` remonté à l'appelant — même correction que sur les
// contacts/le journal/la messagerie (piège `estCal` du 06/08) : un refus de
// règle rendait un espace SANS AUCUN calendrier, indiscernable d'un espace qui
// n'en a simplement pas encore créé.
export function ecouterCalendriersSuite(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'calendriers'), where('espaceId', '==', espaceId));
  return onSnapshot(q,
    snap => callback(snap.docs.map(d => ({ id: d.id, source: 'suite', ...d.data() }))),
    (e) => { callback([]); onErreur?.(e?.message || String(e)); });
}

// Calendriers de PLUSIEURS espaces (ceux que je possède) — temps réel.
// Firestore `in` : max 10 ids ; au-delà, on tronque (un propriétaire a rarement
// plus de 10 espaces).
export function ecouterCalendriersParEspaces(espaceIds, callback, onErreur) {
  const ids = (espaceIds || []).filter(Boolean).slice(0, 10);
  if (ids.length === 0) { callback([]); return () => {}; }
  const q = query(collection(db, 'calendriers'), where('espaceId', 'in', ids));
  return onSnapshot(q,
    snap => callback(snap.docs.map(d => ({ id: d.id, source: 'suite', ...d.data() }))),
    (e) => { callback([]); onErreur?.(e?.message || String(e)); });
}

// Calendriers où J'AI un accès accordé (matrice d'accès), quel que soit l'espace.
// Nécessite le champ miroir `accesUids` (clés de `acces`).
export function ecouterCalendriersAcces(uid, callback, onErreur) {
  if (!uid) { callback([]); return () => {}; }
  const q = query(collection(db, 'calendriers'), where('accesUids', 'array-contains', uid));
  return onSnapshot(q,
    snap => callback(snap.docs.map(d => ({ id: d.id, source: 'acces', ...d.data() }))),
    (e) => { callback([]); onErreur?.(e?.message || String(e)); });
}

// Lit des calendriers par ids (pour les calendriers MoPCal adoptés).
export async function lireCalendriersParIds(ids) {
  const out = [];
  for (const id of (ids || [])) {
    try {
      const snap = await getDoc(doc(db, 'calendriers', id));
      if (snap.exists()) out.push({ id, source: 'mopcal', existe: true, ...snap.data() });
      else out.push({ id, source: 'mopcal', existe: false, nom: '(calendrier introuvable)' });
    } catch { out.push({ id, source: 'mopcal', existe: false, nom: '(inaccessible)' }); }
  }
  return out;
}

// Calendrier « Général » par défaut d'un espace — id DÉTERMINISTE (`gen_{espaceId}`)
// → idempotent : deux appels concurrents écrivent le MÊME doc (jamais de doublon).
export async function assurerCalendrierDefaut(espaceId, auteur) {
  if (!espaceId) return null;
  const id = `gen_${espaceId}`;
  await setDoc(doc(db, 'calendriers', id), {
    espaceId, nom: 'Général', emoji: '📅', type: 'general',
    creePar: auteur?.uid || null, creeParNom: auteur?.nom || '',
    creeLe: new Date().toISOString(),
  }, { merge: true });
  return id;
}

export async function creerCalendrierSuite(espaceId, { nom, emoji, type = 'general', couleur }, auteur) {
  const data = {
    espaceId, nom: nom.trim(), emoji: emoji || '📅', type,
    // PRIVÉ PAR DÉFAUT (sécurité) : `accesN` matérialisé VIDE → seul le
    // propriétaire voit ; on ouvre ensuite au cas par cas (Profil → Qui voit
    // quoi). Sans ce champ, le repli rétro-compat MoPCal ouvrirait à tous.
    accesN: {}, accesTous: false,
    acces: {}, accesUids: [],   // matrice d'accès + miroir requêtable (vide au départ)
    creePar: auteur?.uid || null, creeParNom: auteur?.nom || '',
    creeLe: new Date().toISOString(),
  };
  if (couleur) data.couleur = couleur;
  const ref = await addDoc(collection(db, 'calendriers'), data);
  // Règle produit (Cédric) : TOUT calendrier a une discussion « Générale ».
  // Elle se pose ICI, à la naissance — et non plus seulement à la première
  // ouverture de la messagerie, qui laissait sans Générale tout calendrier
  // jamais ouvert (6 sur 13 en base le 09/08).
  // Volontairement NON bloquant : un échec ici ne doit pas empêcher la création
  // du calendrier lui-même. Le rattrapage à l'ouverture reste en place, et
  // `scripts/semer-generales.js` répare l'existant.
  await assurerGenerale(cleCible('calendrier', ref.id), {
    espaceId, uid: auteur?.uid || null, nom: auteur?.nom || '',
  }).catch(() => {});
  return ref.id;
}

export async function renommerCalendrier(id, { nom, emoji, type }) {
  const champs = {};
  if (nom != null) champs.nom = nom.trim();
  if (emoji != null) champs.emoji = emoji;
  if (type != null) champs.type = type;
  await updateDoc(doc(db, 'calendriers', id), champs);
}

// Supprimer un calendrier detruit TOUT ce qui y est rattache (cf.
// suppressionCalendrier.js). Un simple deleteDoc laissait des orphelins
// invisibles et irrecuperables — c'est ainsi que des budgets ont disparu de
// l'interface tout en restant en base.
export async function supprimerCalendrierSuite(id, auteur) {
  await supprimerCalendrierCascade(id, auteur);
}

// Type du calendrier ('general' | 'coparentalite'). Le type co-parentalité
// active les fonctions de garde ; sinon elles sont désactivées à l'affichage.
export async function definirTypeCalendrier(id, type) {
  await updateDoc(doc(db, 'calendriers', id), { type });
}

// ── ACCÈS UNIFIÉ (grammaire 3 niveaux du Core) ──
// Source des niveaux : `accesN` = { [uid | '*'] : 'lecteur'|'contributeur'|'gestionnaire' }.
// L'ancienne matrice `acces` = { [uid]: { voir, ecrire, config } } reste écrite
// (DÉRIVÉE de accesN) pour rétro-compat, ainsi que le miroir `accesUids`
// (array-contains) — matérialisé avec les membres si '*' est présent.

// Carte des niveaux d'un calendrier. Rétro-compat : sans `accesN`, on convertit
// l'ancienne matrice ET on préserve le comportement historique « les membres de
// l'espace voient + écrivent par défaut » via { '*': 'contributeur' }.
// RÉTRO-COMPAT UNIQUEMENT : un calendrier hérité de MoPCal (aucun `accesN`)
// était ouvert à tous les participants → on préserve ce comportement pour ne
// pas couper l'accès existant. Les calendriers créés DANS la suite portent
// `accesN: {}` dès la création : privé par défaut.
export function accesCalendrierN(cal) {
  if (cal?.accesN && typeof cal.accesN === 'object' && !Array.isArray(cal.accesN)) return cal.accesN;
  return { '*': 'contributeur', ...accesDepuisMatrice(cal?.acces) };
}

// Matrice { voir, ecrire, config } d'un niveau (pour le miroir rétro-compat).
const matriceDeNiveau = (n) => ({
  voir: rangNiveau(n) >= 1, ecrire: rangNiveau(n) >= 2, config: rangNiveau(n) >= 3,
});

// Enregistre la carte d'accès complète (depuis SectionAcces dans Profil).
export async function definirAccesCalendrierN(id, accesN, membresUids = []) {
  const a = accesN || {};
  const tous = rangNiveau(a['*']) >= 1;
  // Matrice rétro-compat : entrées nominatives ; si '*', chaque membre non listé
  // hérite du niveau de '*'.
  const matrice = {};
  Object.keys(a).forEach(k => { if (k !== '*' && rangNiveau(a[k]) >= 1) matrice[k] = matriceDeNiveau(a[k]); });
  if (tous) membresUids.forEach(u => { if (u && !matrice[u]) matrice[u] = matriceDeNiveau(a['*']); });
  const accesUids = Object.keys(matrice).filter(uid => matrice[uid].voir);
  await setDoc(doc(db, 'calendriers', id), {
    accesN: a, accesTous: tous, acces: matrice, accesUids,
  }, { merge: true });
}

// Migration douce : matérialise `accesN` sur un calendrier qui ne l'a pas.
// Idempotent ; ignore les échecs de droits.
export async function assurerAccesCalendrier(cal, membresUids = []) {
  if (!cal?.id || (cal.accesN && typeof cal.accesN === 'object')) return;
  try { await definirAccesCalendrierN(cal.id, accesCalendrierN(cal), membresUids); } catch (_) {}
}

// ── Ancienne API (matrice) — conservée pour compat ──
export async function lireAccesCalendrier(id) {
  try { const s = await getDoc(doc(db, 'calendriers', id)); return s.exists() ? (s.data().acces || {}) : {}; }
  catch { return {}; }
}
export async function definirAccesCalendrier(id, acces) {
  const a = acces || {};
  const accesUids = Object.keys(a).filter(uid => a[uid] && a[uid].voir);
  await setDoc(doc(db, 'calendriers', id), { acces: a, accesUids }, { merge: true });
}
